import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useEventContext } from "Bands/contexts";
import { EventGuestlistBlockService } from "Bands/services";
import { dataMetaSelector, downloadEventGuestlistBlock } from "Bands/state";
import {
  BAND_ACTIONS,
  IEventGuestStatus,
  IGuestlistBlock,
  IGuestlistBlockStatus,
} from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { snakeCase } from "lodash-es";
import { copy } from "shared/assets";
import { FormBuilder, Message, Modal } from "shared/components";
import { ToolbarItems } from "shared/components/Form/Fields/RTE/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { IAPIState, IFieldType, IFormButtons, IStatus } from "shared/types";
import { downloadFile } from "shared/utils";

const downloadCopy = copy.guestlistBlock.download;

interface DownloadGuestlistBlockModalProps {
  block: IGuestlistBlock;
  handleClose: () => void;
}

export const DownloadGuestlistBlockModal: React.FC<DownloadGuestlistBlockModalProps> = ({
  block,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { event } = useEventContext();
  const { api: apiMeta, data } = useSelector(
    dataMetaSelector(BAND_ACTIONS.downloadEventGuestlistBlock)
  );

  const [showPendingWarning, setShowPendingWarning] = useState(false);

  useClearActionMetaOnUnMount(BAND_ACTIONS.downloadEventGuestlistBlock);

  useEffect(() => {
    if (data && apiMeta?.status === IAPIState.succeeded) {
      handleClose();

      downloadFile(data.pdfUrl, `${snakeCase(`${event?.title} ${block.title}`)}.pdf`);

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message:
            block.status === IGuestlistBlockStatus.open
              ? downloadCopy.messages.downloadAndCloseSuccess
              : downloadCopy.messages.downloadSuccess,
        })
      );
    }
  }, [apiMeta?.status, block.status, block.title, data, dispatch, event?.title, handleClose]);

  const handleSubmit = ({ printNote }: { printNote?: string }) => {
    dispatch(
      downloadEventGuestlistBlock({
        id: block.id,
        printNote,
        blockId: block.id,
      })
    );
  };

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: downloadCopy.buttons.download,
        "data-testid": "download_guestlist",
      },
    }),
    []
  );

  useEffect(() => {
    void (async () => {
      const data = await EventGuestlistBlockService.getGuests({
        guestListBlock: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(block.id),
        status: IEventGuestStatus.pending,
      });

      setShowPendingWarning(data?.pagination?.totalItems > 0);
    })();
  }, [block.id]);

  return (
    <Modal
      title={downloadCopy.title}
      dataTestId="download_guestlist_modal"
      handleClose={handleClose}
    >
      <Spacer size="m">
        {showPendingWarning && (
          <Message status={IStatus.warning}>
            <strong style={{ fontWeight: "bold" }}>
              {copy.guestlistBlock.download.messages.pendingRequestsTitle}
            </strong>
            <p>{copy.guestlistBlock.download.messages.pendingRequestsMessage}</p>
          </Message>
        )}
        <p>
          {block.status === IGuestlistBlockStatus.open
            ? downloadCopy.messages.downloadAndClose
            : downloadCopy.messages.download}
        </p>
        <FormBuilder
          defaultValues={{
            printNote: block.printNote,
          }}
          fields={[
            {
              type: IFieldType.rte,
              excludeItems: {
                [ToolbarItems.list]: true,
                [ToolbarItems.link]: true,
              },
              name: "printNote",
              label: downloadCopy.properties.printNote,
            },
          ]}
          onSubmit={handleSubmit}
          buttons={buttons}
          isSubmitting={apiMeta?.status === IAPIState.pending}
          apiError={apiMeta?.error}
          apiErrorMessage={!!apiMeta?.error}
        />
      </Spacer>
    </Modal>
  );
};
